/**
 * Reads CSS custom properties from `:root` so chart colors track the active
 * theme (dark / light). Mirrors the `cssVar()` helper in the legacy
 * `static/analysis.js`. Falls back to hard-coded hex values when
 * `getComputedStyle` returns empty (jsdom under tests).
 */

import { useMemo } from "react";

export interface Palette {
  accent: string;
  success: string;
  warning: string;
  danger: string;
  muted: string;
  text: string;
  border: string;
  e5: string;
  e10: string;
}

const FALLBACK: Palette = {
  accent: "#7aa2ff",
  success: "#4ade80",
  warning: "#f59e0b",
  danger: "#f47171",
  muted: "#6d7588",
  text: "#e8eaf0",
  border: "#262b38",
  e5: "#f59e0b",
  e10: "#22d3ee",
};

function readVar(name: string, fallback: string): string {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

export function usePalette(): Palette {
  return useMemo(
    () => ({
      accent: readVar("--accent", FALLBACK.accent),
      success: readVar("--success", FALLBACK.success),
      warning: readVar("--fuel-e5", FALLBACK.warning),
      danger: readVar("--danger", FALLBACK.danger),
      muted: readVar("--ink-muted", FALLBACK.muted),
      text: readVar("--ink", FALLBACK.text),
      border: readVar("--border", FALLBACK.border),
      e5: readVar("--fuel-e5", FALLBACK.e5),
      e10: readVar("--fuel-e10", FALLBACK.e10),
    }),
    [],
  );
}
